import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { swal, typeChecker } from "../../../utils/methods";
import { _post } from "../../../utils/network";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';

const AdminsCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const formRef = useRef(null);
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('admins'), link: '/admins-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [createButtonIsLoading, setCreateButtonIsLoading] = useState(false);
	const createAdmin = async (event) => {
		event.preventDefault();
		if(createButtonIsLoading)
			return 0;
		let mobile = event.target.adminMobile.value.trim();
		if(!typeChecker(mobile, 'title'))
			return toast.warn(t('enter_mobile_number_correctly'));
		// let password = event.target.adminPassword ? event.target.adminPassword?.value?.trim() : 'null';
		// if(!typeChecker(password, 'title'))
		// 	return toast.warn(t('password') + ' ' + t('must_have_at_least_3_characters'));
		let permissions = ['Self', 'Modules', 'Intros', 'Portfolios', 'Trades', 'Stockcategories', 'Signalorders', 'Logs'];
		if(event.target.adminPermissionAdmins.checked){
			permissions.push('Admin');
			permissions.push('Contents');
		}
		if(event.target.adminPermissionUserCategories.checked)
			permissions.push('Usercategories');
		if(event.target.adminPermissionUsers.checked)
			permissions.push('Users');
		if(event.target.adminPermissionCredits.checked)
			permissions.push('Credits');
		if(event.target.adminPermissionMarketingCodes.checked)
			permissions.push('Codes');
		if(event.target.adminPermissionReferralChanges.checked)
			permissions.push('ReferralChanges');
		if(event.target.adminPermissionRequestCategories.checked)
			permissions.push('Requestcategories');
		if(event.target.adminPermissionRequests.checked)
			permissions.push('Requests');
		if(event.target.adminPermissionStocks.checked)
			permissions.push('Stocks');
		if(event.target.adminPermissionSignals.checked)
			permissions.push('Signals');
		if(event.target.adminPermissionSignalComments.checked)
			permissions.push('Signalcomments');
		if(event.target.adminPermissionConsultants.checked)
			permissions.push('Consultants');
		if(event.target.adminPermissionConsultationCategories.checked)
			permissions.push('Consultationcategories');
		if(event.target.adminPermissionConsultations.checked)
			permissions.push('Consultations');
		if(event.target.adminPermissionProductCategories.checked)
			permissions.push('Productcategories');
		if(event.target.adminPermissionProducts.checked)
			permissions.push('Products');
		if(event.target.adminPermissionProductOrders.checked)
			permissions.push('Orders');
		if(event.target.adminPermissionContests.checked)
			permissions.push('Contests');
		if(event.target.adminPermissionForecasts.checked)
			permissions.push('Contestenrolls');
		if(event.target.adminPermissionTheBests.checked)
			permissions.push('TopCompetitors');
		if(event.target.adminPermissionBanners.checked)
			permissions.push('Banners');
		if(event.target.adminPermissionTransactionCategories.checked)
			permissions.push('Transactioncategories');
		if(event.target.adminPermissionTransactions.checked)
			permissions.push('Transactions');
		let body = {
			admin_id: id,
			username: mobile,
			// password,
			permissions
		};
		confirmSwal(async () => {
			setCreateButtonIsLoading(true);
			let response = await _post('/admins/admin/' + ((componentType === 'create') ? 'add' : componentType), body);
			setCreateButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					if(componentType === 'create')
						event.target.reset();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const [deleteButtonIsLoading, setDeleteButtonIsLoading] = useState(false);
	const deleteAdmin = async () => {
		if(deleteButtonIsLoading)
			return 0;
		let body = {
			admin_id: id
		};
		confirmSwal(async () => {
			setDeleteButtonIsLoading(true);
			let response = await _post('/admins/admin/delete', body);
			setDeleteButtonIsLoading(false);
			switch(response.code){
				case 200:
					navigate("/admins-list");
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const [admin, setAdmin] = useState({});
	const getAdmin = async () => {
		let body = {
			admin_id: id
		};
		let response = await _post('/admins/admin/getOne', body);
		switch(response.code){
			case 200:
				setAdmin(response.data.admin);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	useEffect(() => {
		if(componentType === 'edit')
			getAdmin();
	}, [componentType]);

	useEffect(() => {
		if(componentType === 'create')
			formRef.current.reset();
	}, [location]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t(componentType)} {t('admin')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={createAdmin} ref={formRef}>
						<div className="row gx-2">
							<FormField fieldKey="adminMobile" title={t('mobile')} value={admin?.username} required ltr />
							{/* {componentType === 'create' ?	
								<FormField type="password" fieldKey="adminPassword" title={t('password')} required ltr />
							: ''} */}
							<h6 className="mt-3 mb-4">{t('permissions')}:</h6>
							<FormField type="toggle" fieldKey="adminPermissionAdmins" title={t('admins')} value={admin?.permissions?.includes('Admin')} />
							<FormField type="toggle" fieldKey="adminPermissionUserCategories" title={t('user_categories')} value={admin?.permissions?.includes('Usercategories')} />
							<FormField type="toggle" fieldKey="adminPermissionUsers" title={t('users')} value={admin?.permissions?.includes('Users')} />
							<FormField type="toggle" fieldKey="adminPermissionCredits" title={t('credits')} value={admin?.permissions?.includes('Credits')} />
							<FormField type="toggle" fieldKey="adminPermissionMarketingCodes" title={t('marketing_codes')} value={admin?.permissions?.includes('Codes')} />
							<FormField type="toggle" fieldKey="adminPermissionReferralChanges" title={t('referral_changes')} value={admin?.permissions?.includes('ReferralChanges')} />
							<FormField type="toggle" fieldKey="adminPermissionRequestCategories" title={t('request_categories')} value={admin?.permissions?.includes('Requestcategories')} />
							<FormField type="toggle" fieldKey="adminPermissionRequests" title={t('requests')} value={admin?.permissions?.includes('Requests')} />
							<FormField type="toggle" fieldKey="adminPermissionStocks" title={t('stocks')} value={admin?.permissions?.includes('Stocks')} />
							<FormField type="toggle" fieldKey="adminPermissionSignals" title={t('signals')} value={admin?.permissions?.includes('Signals')} />
							<FormField type="toggle" fieldKey="adminPermissionSignalComments" title={t('signal_comments')} value={admin?.permissions?.includes('Signalcomments')} />
							<FormField type="toggle" fieldKey="adminPermissionConsultants" title={t('consultants')} value={admin?.permissions?.includes('Consultants')} />
							<FormField type="toggle" fieldKey="adminPermissionConsultationCategories" title={t('consultation_categories')} value={admin?.permissions?.includes('Consultationcategories')} />
							<FormField type="toggle" fieldKey="adminPermissionConsultations" title={t('consultations')} value={admin?.permissions?.includes('Consultations')} />
							<FormField type="toggle" fieldKey="adminPermissionProductCategories" title={t('product_categories')} value={admin?.permissions?.includes('Productcategories')} />
							<FormField type="toggle" fieldKey="adminPermissionProducts" title={t('products')} value={admin?.permissions?.includes('Products')} />
							<FormField type="toggle" fieldKey="adminPermissionProductOrders" title={t('product_orders')} value={admin?.permissions?.includes('Orders')} />
							<FormField type="toggle" fieldKey="adminPermissionContests" title={t('contests')} value={admin?.permissions?.includes('Contests')} />
							<FormField type="toggle" fieldKey="adminPermissionForecasts" title={t('forecasts')} value={admin?.permissions?.includes('Contestenrolls')} />
							<FormField type="toggle" fieldKey="adminPermissionTheBests" title={t('the_bests')} value={admin?.permissions?.includes('TopCompetitors')} />
							<FormField type="toggle" fieldKey="adminPermissionBanners" title={t('banners')} value={admin?.permissions?.includes('Banners')} />
							<FormField type="toggle" fieldKey="adminPermissionTransactionCategories" title={t('transaction_categories')} value={admin?.permissions?.includes('Transactioncategories')} />
							<FormField type="toggle" fieldKey="adminPermissionTransactions" title={t('transactions')} value={admin?.permissions?.includes('Transactions')} />
						</div>
						<div>
							{componentType === 'create' ?
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={createButtonIsLoading}>
									<i className="fa fa-save"></i> {t('create')}
								</LoadingButton>
							:
								<>
									<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={createButtonIsLoading}>
										<i className="fa fa-save"></i> {t('update')}
									</LoadingButton>
									<LoadingButton className="btn btn-danger d-inline-block mt-3" onClick={deleteAdmin} type="button" name="delete" isLoading={deleteButtonIsLoading}>
										<i className="fa fa-trash"></i> {t('delete')}
									</LoadingButton>
								</>
							}
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default AdminsCreate;
